import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { SystemTypeEnum, SystemTypeIdEnum } from 'src/app/domain/registration/enums/system-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { PartnerService } from '../../../partners/crud-service';
import { PartnerIndexMultiSelectComponent } from '../../../partners/crud-index/crud-index-multiselect.component';
import { SystemModuleService } from '../../../system-modules/crud-service';
import { SystemService } from '../../../systems/crud-service';
import { PlanService } from '../../crud-service';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';

export enum AccessTypeEnum {
  LOGIN = 'Login',
  TERMINAL = 'Terminal',
  APP = 'App',
  PDV = 'PDV',
  OS = 'OS',
  OUTRO = 'Outro',
}

@Component({
  selector: 'app-plan-creation-dialog',
  templateUrl: './plan-creation-dialog.component.html',
  styleUrls: ['./plan-creation-dialog.component.sass'],
  providers: [
    PlanService,
    SystemService,
    SystemModuleService,
    PartnerService,
  ],
})
export class PlanCreationDialogComponent implements OnInit, OnDestroy {
  private readonly DEFAULT_TYPE = TypeCesEnum.NEW;
  private readonly ALL_PARTNERS_OPTION = '__ALL_PARTNERS__';
  form: FormGroup;
  systems: any[] = [];
  partners: any[] = [];
  selectedPartners: any[] = [];
  modules: any[] = [];
  selectedModules: any[] = [];
  variations: any[] = [];
  variationCounter = 1;
  today = new Date();

  systemTypeOptions = Object.values(SystemTypeEnum);
  planTypeOptions: Array<{ id: string; code: string; name: string }> = [];
  accessTypeOptions = Object.values(AccessTypeEnum);
  yesNoOptions = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false },
  ];

  variationsDataSource = new MatTableDataSource<any>([]);
  variationColumns = [
    'codigo',
    'tipoSolucao',
    'solucao',
    'status',
    'valorMensalidade',
    'custo',
    'acoes',
  ];
  editingVariationIndex: number | null = null;
  moduleItemText = '';
  includedItemText = '';
  excludedItemText = '';
  moduleItems: string[] = [];
  includedItems: string[] = [];
  excludedItems: string[] = [];

  private destroy$ = new Subject<void>();
  private pendingPartnerIds: string[] = [];
  public partnerSelectionMode: 'ALL' | 'SELECT' | null = 'SELECT';
  private isHydratingInitialData = false;
  private isEditingVariationContext = false;
  private loadedSystemsType: string | null = null;
  private loadedModulesSystemId: string | null = null;
  private lastPlanTypeSelection = 'NORMAL';
  private isSwitchingToResalePlan = false;

  private normalizeId(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<PlanCreationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private systemService: SystemService,
    private systemModuleService: SystemModuleService,
    private planService: PlanService,
    private partnerService: PartnerService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    if (this.isPartnerUser) {
      this.variationColumns = this.variationColumns.filter((column) => column !== 'custo');
    }
    this.buildForm();
    this.setupSubscriptions();
    this.initializeFromData();
    this.loadPlanTypes();
    this.loadPartners();
    if (this.isShowMode) {
      this.form.disable({ emitEvent: false });
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      planType: ['NORMAL', Validators.required],
      type: ['', Validators.required],
      payCommission: [false],
      isDaysCount: [true],
      hasAdhesion: [false],
      htmlEnabled: [true],
      partners: [[]],
      system: ['', Validators.required],
      systemId: [''],
      dueDate: [''],
      accessQuantity: [1, [Validators.required, Validators.min(1)]],
      accessType: [AccessTypeEnum.TERMINAL, Validators.required],
      cost: [0, Validators.required],
      adhesionValue: [0],
      valuePerAccess: [0, Validators.required],
      description: [''],
    });
  }

  private setupSubscriptions(): void {
    this.form.get('planType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((planType) => {
        const normalizedPlanType = String(planType || '').trim().toUpperCase();
        const previousPlanType = String(this.lastPlanTypeSelection || '').trim().toUpperCase() || 'NORMAL';

        if (
          previousPlanType !== 'REVENDA' &&
          normalizedPlanType === 'REVENDA' &&
          this.hasMoreThanOneVariationSolution()
        ) {
          this.form.patchValue({ planType: previousPlanType || 'NORMAL' }, { emitEvent: false });
          this.dialog.open(DialogConfirmComponent, {
            width: '420px',
            data: {
              title: 'Atenção',
              content:
                'O tipo REVENDA aceita apenas uma solução. Remova variações com soluções diferentes antes de alterar o tipo do plano.',
              yesText: 'Continuar',
              noText: ''
            }
          });
          return;
        }

        this.lastPlanTypeSelection = normalizedPlanType || previousPlanType;

        if (normalizedPlanType === 'REVENDA') {
          const resaleSolution = this.getResaleSolutionFromVariations();
          if (resaleSolution.systemId || resaleSolution.systemName) {
            this.form.patchValue(
              {
                system: resaleSolution.systemId || resaleSolution.systemName,
                systemId: resaleSolution.systemId || '',
              },
              { emitEvent: false }
            );
          }
          this.isSwitchingToResalePlan = true;
          this.form.patchValue({ type: SystemTypeEnum.REVENDA }, { emitEvent: true });
        }
      });

    this.form.get('type')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => {
        if (this.isHydratingInitialData) {
          if (type) {
            this.loadSystems(type);
          }
          return;
        }

        if (this.isSwitchingToResalePlan) {
          this.isSwitchingToResalePlan = false;
          if (type) {
            this.loadSystems(type);
          }
          return;
        }

        this.form.patchValue({ system: '', systemId: '' });
        this.systems = [];
        this.modules = [];
        this.selectedModules = [];
        if (type) {
          this.loadSystems(type);
        }
      });

    this.form.get('system')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((systemValue) => {
        const found = this.systems.find(
          (s) => String(s.id) === String(systemValue) || s.name === systemValue
        );
        if (found) {
          this.form.patchValue({ systemId: found.id }, { emitEvent: false });
          this.loadModules(found.id, this.editingVariationIndex !== null);
        } else {
          this.modules = [];
          this.selectedModules = [];
        }
      });

    this.form.get('hasAdhesion')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasAdhesion) => {
        if (!hasAdhesion) {
          this.form.patchValue({ adhesionValue: 0 }, { emitEvent: false });
        }
      });

  }

  private loadPlanTypes(): void {
    this.planService
      .findPlanTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows: any[]) => {
          const list = Array.isArray(rows) ? rows : [];
          this.planTypeOptions = list;

          const control = this.form.get('planType');
          const currentValue = String(control?.value || '').trim();
          const hasCurrent = this.planTypeOptions.some(
            (item) =>
              String(item?.code || '').toUpperCase() === currentValue.toUpperCase() ||
              String(item?.id || '') === currentValue
          );

          if (!hasCurrent) {
            const normal = this.planTypeOptions.find(
              (item) => String(item?.code || '').toUpperCase() === 'NORMAL'
            );
            control?.setValue(normal?.code || this.planTypeOptions[0]?.code || 'NORMAL', { emitEvent: false });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.planTypeOptions = [
            { id: 'NORMAL', code: 'NORMAL', name: 'Normal' },
            { id: 'REVENDA', code: 'REVENDA', name: 'Revenda' },
          ];
          this.cdr.markForCheck();
        },
      });
  }

  private loadSystems(type: string): void {
    const typeId = SystemTypeIdEnum[type as keyof typeof SystemTypeIdEnum];
    this.systemService
      .listAll({ limit: 5000, page: 0, filter: '', type: typeId })
      .pipe(takeUntil(this.destroy$))
      .subscribe(([data]: any) => {
        this.systems = data || [];
        this.loadedSystemsType = String(type || '').trim();
        const currentSystemValue = this.form.get('system')?.value;
        const currentSystemId = String(this.form.get('systemId')?.value || '').trim();
        const found = this.systems.find(
          (s: any) =>
            String(s.id) === String(currentSystemValue) ||
            String(s.id) === currentSystemId ||
            s.name === currentSystemValue
        );

        if (found) {
          this.form.patchValue(
            { system: found.id, systemId: found.id },
            { emitEvent: false }
          );
          this.loadModules(found.id, this.editingVariationIndex !== null);
        }
        this.cdr.markForCheck();
      });
  }

  private loadModules(systemId: string, preserveSelectedModules = false): void {
    this.systemModuleService
      .findBySystem(systemId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        const source = Array.isArray(data) ? data : [];
        this.modules = source
          .map((item: any) => ({
            ...item,
            id: item?.id || item?.systemModuleId || item?.moduleId || '',
            name: item?.name || item?.description || item?.moduleName || '',
          }))
          .filter((item: any) => !!item.id && !!item.name);
        this.loadedModulesSystemId = String(systemId || '').trim();
        if (!this.isHydratingInitialData && !this.isEditingVariationContext && !preserveSelectedModules) {
          this.selectedModules = [];
        }
        this.cdr.markForCheck();
      });
  }

  public onModulesOpened(opened: boolean): void {
    if (!opened) return;
    if (Array.isArray(this.modules) && this.modules.length > 0) return;

    const systemId = this.resolveSystemIdFromSelection(
      this.form.get('systemId')?.value,
      this.form.get('system')?.value
    );
    if (!systemId) return;
    this.loadModules(systemId);
  }


  private loadPartners(): void {
    this.partnerService
      .listAll({ limit: 5000, page: 0, filter: '' })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        const partnerList = this.extractCollection(response);
        this.partners = Array.isArray(partnerList) ? partnerList : [];

        if (this.partners.length === 0) {
          this.partnerService
            .getAllPartners({ limit: 1000, page: 0, filter: '' })
            .pipe(takeUntil(this.destroy$))
            .subscribe((fallbackResponse: any) => {
              this.partners = this.extractCollection(fallbackResponse);
              this.applyPartnerSelectionState();
            });
          return;
        }

        this.applyPartnerSelectionState();
      });
  }

  private selectAllPartners(): void {
    if (!Array.isArray(this.partners) || this.partners.length === 0) {
      return;
    }

    this.selectedPartners = [...this.partners];
    this.form.patchValue({ partners: this.selectedPartnerIds }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  private applyPartnerSelectionState(): void {
    if (this.pendingPartnerIds.length > 0) {
      this.selectedPartners = this.partners.filter((partner) =>
        this.pendingPartnerIds.includes(String(partner?.id))
      );
      this.pendingPartnerIds = [];
    }

    if (this.selectedPartners.length > 0) {
      this.form.patchValue({ partners: this.selectedPartnerIds }, { emitEvent: false });
    }

    this.partnerSelectionMode = this.areAllPartnersSelected() ? 'ALL' : 'SELECT';

    this.cdr.markForCheck();
  }

  private extractCollection(response: any): any[] {
    if (Array.isArray(response)) {
      if (response.length > 0 && Array.isArray(response[0])) {
        return response[0];
      }
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (Array.isArray(response.items)) {
      return response.items;
    }

    if (Array.isArray(response.content)) {
      return response.content;
    }

    return [];
  }

  public get selectedModuleIds(): any[] {
    return this.selectedModules.map((module) => module.id);
  }

  public get selectedModuleId(): any | null {
    return this.selectedModules.length > 0 ? this.selectedModules[0]?.id : null;
  }

  public get variationModules(): any[] {
    const moduleById = new Map<string, any>();
    (this.variations || []).forEach((variation: any) => {
      const modules = Array.isArray(variation?.modules) ? variation.modules : [];
      modules.forEach((module: any) => {
        const id = String(module?.id || '');
        if (!id || moduleById.has(id)) return;
        moduleById.set(id, module);
      });
    });
    return Array.from(moduleById.values());
  }

  public onModulesSelectionChange(selectedIds: any[]): void {
    const ids = Array.isArray(selectedIds) ? selectedIds : [];
    this.selectedModules = this.modules.filter((module) => ids.includes(module.id));
  }

  public onModuleSelectionChange(selectedId: any): void {
    const id = String(selectedId || '').trim();
    if (!id) {
      this.selectedModules = [];
      return;
    }

    if (this.isModuleAlreadyUsedInAnotherVariation(id, this.editingVariationIndex)) {
      this.selectedModules = [];
      this.cdr.markForCheck();
      return;
    }

    const selected = this.modules.find(
      (module: any) => String(module?.id || '').trim() === id
    );

    this.selectedModules = selected ? [selected] : [];
  }

  public get availableModulesForSelection(): any[] {
    const usedModuleIds = new Set<string>();

    (this.variations || []).forEach((variation: any, index: number) => {
      if (this.editingVariationIndex !== null && index === this.editingVariationIndex) return;
      const modules = Array.isArray(variation?.modules) ? variation.modules : [];
      modules.forEach((module: any) => {
        const id = this.normalizeId(module?.id || module?.systemModuleId || module?.moduleId);
        if (id) usedModuleIds.add(id);
      });
    });

    return (this.modules || []).filter((module: any) => {
      const moduleId = this.normalizeId(module?.id || module?.systemModuleId || module?.moduleId);
      if (!moduleId) return false;
      return !usedModuleIds.has(moduleId);
    });
  }

  public get selectedPartnerIds(): string[] {
    return this.selectedPartners
      .map((partner: any) => String(partner?.id || ''))
      .filter((id: string) => !!id);
  }

  public onPartnersSelectionChange(selectedIds: string[]): void {
    const ids = Array.isArray(selectedIds) ? selectedIds.map((id) => String(id)) : [];
    if (ids.includes(this.ALL_PARTNERS_OPTION)) {
      this.selectAllPartners();
      return;
    }
    this.selectedPartners = this.partners.filter((partner) => ids.includes(String(partner?.id)));
  }

  public onPartnerSelectionModeChange(mode: 'ALL' | 'SELECT'): void {
    this.partnerSelectionMode = mode || 'SELECT';
    if (this.partnerSelectionMode === 'ALL') {
      this.selectAllPartners();
      return;
    }
    // Reset value so selecting "Selecionar" again always triggers opening modal.
    this.partnerSelectionMode = null;
    this.openPartnersDialog();
  }

  public onSelectPartnersOptionClick(): void {
    if (this.isShowMode || this.isPlanRulesLocked) {
      return;
    }
    this.partnerSelectionMode = null;
    this.openPartnersDialog();
  }

  public openPartnersDialog(): void {
    if (this.isShowMode || this.isPlanRulesLocked) {
      return;
    }

    const ref = this.dialog.open(PartnerIndexMultiSelectComponent, {
      width: '90vw',
      maxWidth: '95vw',
      height: '85vh',
      data: {
        type: TypeIndexEnum.SELECT,
        select: true,
        params: {},
        preselectedIds: this.selectedPartnerIds
      }
    });

    ref.afterClosed().subscribe((result: any[] | undefined) => {
      if (!Array.isArray(result)) {
        return;
      }

      this.selectedPartners = result.map((partner: any) => ({
        id: partner?.id,
        name: partner?.name || partner?.companyName || ''
      }));

      this.form.patchValue({ partners: this.selectedPartnerIds }, { emitEvent: false });
      this.partnerSelectionMode = this.areAllPartnersSelected() ? 'ALL' : 'SELECT';
      this.cdr.markForCheck();
    });
  }

  get descriptionControl(): FormControl {
    return this.form.get('description') as FormControl;
  }

  get canSaveVariation(): boolean {
    if (this.isShowMode) {
      return false;
    }
    const v = this.form.getRawValue();
    const accessQuantity = Number(v.accessQuantity || 0);
    const valuePerAccess = Number(v.valuePerAccess || 0);
    const adhesionValue = Number(v.adhesionValue || 0);

    const resolvedSystemId = this.resolveSystemIdFromSelection(v.systemId, v.system);
    if (!v.name || !v.type || !v.system || !v.accessType) return false;
    if (!resolvedSystemId) return false;
    if (!accessQuantity || accessQuantity < 1) return false;
    if (valuePerAccess <= 0) return false;
    if (v.hasAdhesion && adhesionValue <= 0) return false;
    if (this.selectedModules.length === 0) return false;
    if (this.hasDuplicateModuleInVariations(this.selectedModules, this.editingVariationIndex)) return false;
    return true;
  }

  get canSavePlan(): boolean {
    if (this.isShowMode) {
      return false;
    }
    const name = String(this.form.get('name')?.value || '').trim();
    const hasSystemId = this.variations.every((variation) =>
      !!String(variation?.systemId || '').trim()
    );
    return !!name && this.variations.length > 0 && hasSystemId;
  }

  private resolveSystemIdFromSelection(systemId: any, systemValue: any): string {
    const direct = String(systemId || '').trim();
    if (direct) return direct;

    const byId = this.systems.find((system) => String(system.id) === String(systemValue));
    if (byId?.id) return String(byId.id);

    const byName = this.systems.find(
      (system) =>
        String(system?.name || '').trim().toLowerCase() ===
        String(systemValue || '').trim().toLowerCase()
    );
    return String(byName?.id || '').trim();
  }

  saveVariation(): void {
    const v = this.form.getRawValue();
    const selectedSystemId = this.resolveSystemIdFromSelection(v.systemId, v.system);
    if (!selectedSystemId) {
      return;
    }
    const selectedSystem = this.systems.find(
      (system) => String(system.id) === String(selectedSystemId)
    );
    const selectedSystemName = selectedSystem?.name || v.system || '';
    const currentEditingVariation =
      this.editingVariationIndex !== null
        ? this.variations[this.editingVariationIndex]
        : null;
    const variation = {
      id: currentEditingVariation?.id,
      codigo:
        this.editingVariationIndex !== null
          ? this.variations[this.editingVariationIndex]?.codigo
          : `VAR-${String(this.variationCounter).padStart(3, '0')}`,
      nome: v.name,
      tipoSolucao: v.type,
      solucao: selectedSystemName,
      adesao: v.hasAdhesion,
      diasCorridos: v.isDaysCount,
      acesso: v.accessType,
      qtAcesso: v.accessQuantity,
      vencimento: v.dueDate,
      custo: v.cost,
      adesaoValor: v.adhesionValue,
      valorMensalidade: v.valuePerAccess,
      allPeople: this.areAllPartnersSelected(),
      payCommission: v.payCommission,
      htmlEnabled: v.htmlEnabled,
      systemId: selectedSystemId,
      modules: [...this.selectedModules],
      status: currentEditingVariation?.status || 'ATIVO',
      dataCriacao: new Date(),
    };

    if (this.hasDuplicateModuleInVariations(this.selectedModules, this.editingVariationIndex)) {
      return;
    }

    if (this.editingVariationIndex !== null) {
      this.variations[this.editingVariationIndex] = variation;
      this.editingVariationIndex = null;
    } else {
      this.variations = [...this.variations, variation];
      this.variationCounter++;
    }

    this.variationsDataSource.data = this.variations;
    this.clearVariationFields();
  }

  private hasDuplicateModuleInVariations(modules: any[], editingIndex: number | null): boolean {
    const selectedModuleIds = new Set(
      (Array.isArray(modules) ? modules : [])
        .map((item: any) => String(item?.id || item?.systemModuleId || item?.moduleId || '').trim())
        .filter((id: string) => !!id)
    );

    if (selectedModuleIds.size === 0) {
      return false;
    }

    return this.variations.some((variation: any, index: number) => {
      if (editingIndex !== null && index === editingIndex) return false;
      const variationModuleIds = new Set(
        (Array.isArray(variation?.modules) ? variation.modules : [])
          .map((item: any) => String(item?.id || item?.systemModuleId || item?.moduleId || '').trim())
          .filter((id: string) => !!id)
      );
      for (const selectedId of selectedModuleIds) {
        if (variationModuleIds.has(selectedId)) return true;
      }
      return false;
    });
  }

  private isModuleAlreadyUsedInAnotherVariation(moduleId: string, editingIndex: number | null): boolean {
    const normalizedModuleId = this.normalizeId(moduleId);
    if (!normalizedModuleId) return false;

    return (this.variations || []).some((variation: any, index: number) => {
      if (editingIndex !== null && index === editingIndex) return false;
      const variationModules = Array.isArray(variation?.modules) ? variation.modules : [];
      return variationModules.some((module: any) => {
        const existingId = this.normalizeId(module?.id || module?.systemModuleId || module?.moduleId);
        return existingId === normalizedModuleId;
      });
    });
  }

  removeVariation(index: number): void {
    this.variations.splice(index, 1);
    this.variationsDataSource.data = [...this.variations];
  }

  toggleVariationStatus(index: number): void {
    const variation = this.variations[index];
    if (!variation) {
      return;
    }

    const currentStatus = String(variation.status || '').toUpperCase();
    const nextStatus = currentStatus === 'ATIVO' ? 'INATIVO' : 'ATIVO';
    this.variations[index] = { ...variation, status: nextStatus };
    this.variationsDataSource.data = [...this.variations];
    this.cdr.markForCheck();
  }

  isVariationActive(status: any): boolean {
    return String(status || '').toUpperCase() === 'ATIVO';
  }

  async editVariation(index: number): Promise<void> {
    const variation = this.variations[index];
    if (!variation) {
      return;
    }

    this.editingVariationIndex = index;
    this.isEditingVariationContext = true;

    try {
      const variationType = String(variation?.tipoSolucao || '').trim();
      const variationSystemId = String(variation?.systemId || '').trim();

      if (variationType) {
        await this.loadSystemsForEdit(variationType);
      }

      const resolvedSystemId = this.resolveSystemIdFromSelection(
        variationSystemId,
        variation?.solucao
      );

      if (resolvedSystemId) {
        await this.loadModulesForEdit(resolvedSystemId);
      } else {
        this.modules = [];
      }

      this.selectedModules = this.resolveSelectedModulesFromVariation(variation);

      this.form.patchValue({
        name: variation.nome,
        type: variation.tipoSolucao,
        system: resolvedSystemId || variation.solucao,
        hasAdhesion: variation.adesao,
        isDaysCount: variation.diasCorridos,
        accessType: variation.acesso,
        accessQuantity: variation.qtAcesso,
        dueDate: variation.vencimento,
        cost: variation.custo ?? variation.cost ?? 0,
        adhesionValue: variation.adesaoValor,
        valuePerAccess: variation.valorMensalidade,
        payCommission: variation.payCommission,
        htmlEnabled: variation.htmlEnabled ?? true,
        systemId: resolvedSystemId,
      }, { emitEvent: false });

      this.cdr.detectChanges();
    } finally {
      this.isEditingVariationContext = false;
    }
  }

  private async loadSystemsForEdit(type: string): Promise<void> {
    const normalizedType = String(type || '').trim();
    if (this.loadedSystemsType === normalizedType && Array.isArray(this.systems) && this.systems.length > 0) {
      return;
    }

    const typeId = SystemTypeIdEnum[type as keyof typeof SystemTypeIdEnum];
    if (!typeId) {
      this.systems = [];
      this.loadedSystemsType = null;
      return;
    }

    try {
      const [data] = await firstValueFrom(
        this.systemService.listAll({ limit: 5000, page: 0, filter: '', type: typeId })
      ) as any;
      this.systems = Array.isArray(data) ? data : [];
      this.loadedSystemsType = normalizedType;
    } catch {
      this.systems = [];
      this.loadedSystemsType = null;
    }
  }

  private async loadModulesForEdit(systemId: string): Promise<void> {
    const normalizedSystemId = String(systemId || '').trim();
    if (this.loadedModulesSystemId === normalizedSystemId && Array.isArray(this.modules) && this.modules.length > 0) {
      return;
    }

    try {
      const data = await firstValueFrom(this.systemModuleService.findBySystem(systemId)) as any;
      const source = Array.isArray(data) ? data : [];
      this.modules = source
        .map((item: any) => ({
          ...item,
          id: item?.id || item?.systemModuleId || item?.moduleId || '',
          name: item?.name || item?.description || item?.moduleName || '',
        }))
        .filter((item: any) => !!item.id && !!item.name);
      this.loadedModulesSystemId = normalizedSystemId;
    } catch {
      this.modules = [];
      this.loadedModulesSystemId = null;
    }
  }

  private resolveSelectedModulesFromVariation(variation: any): any[] {
    const variationModules = Array.isArray(variation?.modules) ? variation.modules : [];
    if (variationModules.length === 0) {
      return [];
    }

    const moduleIds = new Set(
      variationModules
        .map((module: any) => String(module?.id || module?.systemModuleId || module?.moduleId || module || '').trim())
        .filter((id: string) => !!id)
    );

    const resolvedFromAvailable = this.modules.filter((module: any) =>
      moduleIds.has(String(module?.id || '').trim())
    );

    if (resolvedFromAvailable.length > 0) {
      return resolvedFromAvailable;
    }

    // Fallback: keep raw module data when not found in available list yet.
    return variationModules.map((module: any) => ({
      ...module,
      id: module?.id || module?.systemModuleId || module?.moduleId || '',
      name: module?.name || module?.description || module?.moduleName || '',
    }));
  }

  private clearVariationFields(preserveSolutionContext = false): void {
    this.selectedModules = [];
    this.modules = [];

    this.form.patchValue(
      {
        ...(preserveSolutionContext ? {} : { type: '', system: '', systemId: '' }),
        cost: 0,
        adhesionValue: 0,
        valuePerAccess: 0,
      },
      { emitEvent: false }
    );

    // Avoid showing "required" red state right after adding a variation.
    const typeControl = this.form.get('type');
    const systemControl = this.form.get('system');
    const systemIdControl = this.form.get('systemId');
    typeControl?.setErrors(null);
    typeControl?.markAsPristine();
    typeControl?.markAsUntouched();
    typeControl?.updateValueAndValidity({ emitEvent: false });
    systemControl?.setErrors(null);
    systemControl?.markAsPristine();
    systemControl?.markAsUntouched();
    systemControl?.updateValueAndValidity({ emitEvent: false });
    systemIdControl?.setErrors(null);
    systemIdControl?.markAsPristine();
    systemIdControl?.markAsUntouched();
    systemIdControl?.updateValueAndValidity({ emitEvent: false });

    this.cdr.markForCheck();
  }

  addIncludedItem(): void {
    const value = this.includedItemText.trim();
    if (!value) {
      return;
    }
    this.includedItems = [...this.includedItems, value];
    this.includedItemText = '';
  }

  addModuleItem(): void {
    const value = this.moduleItemText.trim();
    if (!value) {
      return;
    }
    this.moduleItems = [...this.moduleItems, value];
    this.moduleItemText = '';
  }

  removeModuleItem(index: number): void {
    if (index < 0 || index >= this.moduleItems.length) {
      return;
    }
    this.moduleItems = this.moduleItems.filter((_, idx) => idx !== index);
  }

  removeIncludedItem(index: number): void {
    if (index < 0 || index >= this.includedItems.length) {
      return;
    }
    this.includedItems = this.includedItems.filter((_, idx) => idx !== index);
  }

  addExcludedItem(): void {
    const value = this.excludedItemText.trim();
    if (!value) {
      return;
    }
    this.excludedItems = [...this.excludedItems, value];
    this.excludedItemText = '';
  }

  removeExcludedItem(index: number): void {
    if (index < 0 || index >= this.excludedItems.length) {
      return;
    }
    this.excludedItems = this.excludedItems.filter((_, idx) => idx !== index);
  }

  /**
   * Converte o tipo do plano (nome do enum, ex.: 'APLICAÇÃO') para o id numérico
   * que o backend espera (SystemTypeIdEnum). Aceita valores já numéricos.
   */
  private resolveTypeId(type: any): number | undefined {
    if (type === null || type === undefined || String(type).trim() === '') {
      return undefined;
    }
    if (typeof type === 'number') {
      return type;
    }
    const trimmed = String(type).trim();
    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber) && String(asNumber) === trimmed) {
      return asNumber;
    }
    const mapped = SystemTypeIdEnum[trimmed as keyof typeof SystemTypeIdEnum];
    return typeof mapped === 'number' ? mapped : undefined;
  }

  onSave(): void {
    if (!this.canSavePlan) {
      return;
    }

    const v = this.form.getRawValue();
    const selectedSystemId = this.resolveSystemIdFromSelection(v.systemId, v.system);
    const selectedSystem = this.systems.find(
      (system) => String(system.id) === String(selectedSystemId)
    );
    const selectedSystemName = selectedSystem?.name || v.system || '';
    const firstVariation = Array.isArray(this.variations) ? this.variations[0] : null;
    const firstVariationSystemIdRaw = String(firstVariation?.systemId || '').trim();
    const firstVariationSystemIdByName = !firstVariationSystemIdRaw
      ? this.systems.find((system: any) => String(system?.name || '').trim() === String(firstVariation?.solucao || '').trim())?.id
      : '';
    const firstVariationSystemId = String(firstVariationSystemIdRaw || firstVariationSystemIdByName || '').trim();
    const firstVariationAccessQuantity = Number(firstVariation?.qtAcesso ?? firstVariation?.accessQuantity ?? 0);
    const firstVariationMonthlyValue = Number(firstVariation?.valorMensalidade ?? firstVariation?.valuePerAccess ?? 0);
    const firstVariationAdhesionValue = Number(firstVariation?.adesaoValor ?? firstVariation?.adhesionValue ?? 0);
    const currentElementSystemId = String(
      this.data?.element?.systemId || this.data?.element?.system?.id || ''
    ).trim();
    const currentElementSystemName = String(
      this.data?.element?.system?.name || this.data?.element?.system || ''
    ).trim();
    const resolvedSystemId =
      String(selectedSystemId || '').trim() ||
      firstVariationSystemId ||
      currentElementSystemId ||
      '';
    const shouldIncludeId = this.typeCes === TypeCesEnum.EDIT && !!this.data?.element?.id;
    const resolvedType = String(v.type || '').trim() || String(firstVariation?.tipoSolucao || '').trim();
    const resolvedSystemName =
      String(selectedSystemName || '').trim() ||
      String(firstVariation?.solucao || '').trim() ||
      currentElementSystemName ||
      '';
    const resolvedAccessType =
      String(v.accessType || '').trim() ||
      String(firstVariation?.acesso || '').trim() ||
      '';
    const resolvedIsDaysCount =
      typeof v.isDaysCount === 'boolean'
        ? v.isDaysCount
        : (typeof firstVariation?.diasCorridos === 'boolean' ? firstVariation.diasCorridos : true);
    const resolvedTerminalQuantity = Number(v.accessQuantity || 0) > 0
      ? Number(v.accessQuantity)
      : (firstVariationAccessQuantity > 0 ? firstVariationAccessQuantity : 0);
    const resolvedMonthlyValue = Number(v.valuePerAccess || 0) > 0
      ? Number(v.valuePerAccess)
      : (firstVariationMonthlyValue > 0 ? firstVariationMonthlyValue : 0);
    const resolvedAdhesionFee = Number(v.adhesionValue || 0) >= 0
      ? Number(v.adhesionValue || 0)
      : (firstVariationAdhesionValue >= 0 ? firstVariationAdhesionValue : 0);
    const toPlanVariationPayload = (variation: any) => {
      const payload: any = {
        codigo: variation?.codigo,
        systemId: variation?.systemId,
        ...(variation?.id ? { id: variation.id } : {}),
        modules: Array.isArray(variation?.modules)
          ? variation.modules.map((module: any) => ({
            ...(module?.id ? { id: module.id } : {}),
            ...(module?.name ? { name: module.name } : {}),
          }))
          : [],
      };

      const optionalFields = [
        'nome',
        'tipoSolucao',
        'solucao',
        'adesao',
        'diasCorridos',
        'acesso',
        'qtAcesso',
        'vencimento',
        'custo',
        'adesaoValor',
        'valorMensalidade',
        'allPeople',
        'payCommission',
        'htmlEnabled',
        'status',
      ];

      optionalFields.forEach((field) => {
        if (variation?.[field] !== undefined && variation?.[field] !== null) {
          payload[field] = variation[field];
        }
      });

      return payload;
    };

    this.dialogRef.close({
      element: {
        name: v.name,
        planType: v.planType,
        ...(shouldIncludeId ? { id: this.data?.element?.id } : {}),
        type: this.resolveTypeId(resolvedType),
        systemId: resolvedSystemId,
        isDaysCount: resolvedIsDaysCount,
        adhesionFee: resolvedAdhesionFee,
        monthlyValue: resolvedMonthlyValue,
        terminalQuantity: resolvedTerminalQuantity,
        allPeople: this.areAllPartnersSelected(),
        allPartners: this.areAllPartnersSelected(),
        payCommission: v.payCommission,
        htmlEnabled: v.htmlEnabled,
        dueDate: v.dueDate,
        accessType: resolvedAccessType,
        partners: this.areAllPartnersSelected() ? [] : this.selectedPartnerIds,
        partnersList: this.selectedPartners.map((partner: any) => ({
          id: partner?.id,
          name: partner?.name || '',
        })),
        planDescription: v.description,
        variations: (this.variations || []).map((variation: any) => toPlanVariationPayload(variation)),
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  public get typeCes(): TypeCesEnum {
    return this.data?.type || this.DEFAULT_TYPE;
  }

  public get isShowMode(): boolean {
    return this.typeCes === TypeCesEnum.SHOW;
  }

  public get isPlanRulesLocked(): boolean {
    return this.typeCes === TypeCesEnum.NEW && this.variations.length > 0;
  }

  public get isResalePlan(): boolean {
    const planType = String(this.form?.get('planType')?.value || '').trim().toUpperCase();
    return planType === 'REVENDA';
  }

  public get isResaleSolutionLocked(): boolean {
    return this.isResalePlan && (this.variations?.length || 0) > 0;
  }

  private hasMoreThanOneVariationSolution(): boolean {
    const variations = Array.isArray(this.variations) ? this.variations : [];
    if (variations.length <= 1) return false;

    const solutions = new Set<string>();
    variations.forEach((variation: any) => {
      const systemId = String(variation?.systemId || '').trim();
      const systemName = String(variation?.solucao || '').trim().toLowerCase();
      if (systemId) {
        solutions.add(`id:${systemId}`);
      } else if (systemName) {
        solutions.add(`name:${systemName}`);
      }
    });

    return solutions.size > 1;
  }

  private getResaleSolutionFromVariations(): { systemId: string; systemName: string } {
    const firstVariation = Array.isArray(this.variations) && this.variations.length > 0
      ? this.variations[0]
      : null;

    return {
      systemId: String(firstVariation?.systemId || '').trim(),
      systemName: String(firstVariation?.solucao || '').trim(),
    };
  }

  public get dialogTitle(): string {
    switch (this.typeCes) {
      case TypeCesEnum.EDIT:
        return 'EDIÇÃO DE PLANO';
      case TypeCesEnum.SHOW:
        return 'VISUALIZAÇÃO DE PLANO';
      default:
        return 'CRIAÇÃO DE PLANO';
    }
  }

  private initializeFromData(): void {
    const element = this.data?.element;
    if (!element) {
      return;
    }

    this.isHydratingInitialData = true;

    const planTypeValue = String(
      element?.planType || element?.planTypeCode || 'NORMAL'
    ).trim().toUpperCase();
    this.lastPlanTypeSelection = planTypeValue || 'NORMAL';
    const typeValueFromElement =
      element.typeDescription ||
      element.typeLabel ||
      element.type ||
      '';
    const typeValue =
      planTypeValue === 'REVENDA'
        ? SystemTypeEnum.REVENDA
        : typeValueFromElement;

    const firstVariation = Array.isArray(element?.variations) ? element.variations[0] : null;
    const firstVariationSystemId = String(firstVariation?.systemId || '').trim();
    const firstVariationSystemName = String(firstVariation?.solucao || '').trim();
    const systemName =
      (planTypeValue === 'REVENDA'
        ? (firstVariationSystemName || element?.system?.name || element?.system || '')
        : (element?.system?.name || element?.system || ''));
    const systemId =
      (planTypeValue === 'REVENDA'
        ? (firstVariationSystemId || element?.systemId || element?.system?.id || '')
        : (element?.systemId || element?.system?.id || ''));
    const firstVariationDueDate =
      Array.isArray(element?.variations)
        ? element.variations.find((variation: any) =>
            variation?.vencimento !== null &&
            variation?.vencimento !== undefined &&
            String(variation?.vencimento).trim() !== ''
          )?.vencimento
        : null;

    const resolvedDueDate =
      (element?.dueDate !== null && element?.dueDate !== undefined && String(element?.dueDate).trim() !== '')
        ? element.dueDate
        : (firstVariationDueDate !== null && firstVariationDueDate !== undefined && String(firstVariationDueDate).trim() !== '')
          ? firstVariationDueDate
          : (element?.system?.dueDate ?? '');

    this.form.patchValue(
      {
        name: element?.name || '',
        planType: planTypeValue,
        type: typeValue,
        payCommission: !!element?.payCommission,
        isDaysCount: element?.isDaysCount !== false,
        hasAdhesion: !!element?.hasAdhesion || !!element?.adhesionFee,
        htmlEnabled: element?.htmlEnabled !== false,
        system: systemId || systemName,
        systemId,
        dueDate: resolvedDueDate,
        accessQuantity: element?.terminalQuantity || element?.accessQuantity || 1,
        accessType: element?.accessType || AccessTypeEnum.TERMINAL,
        cost: element?.cost || 0,
        adhesionValue: element?.adhesionFee || element?.adhesionValue || 0,
        valuePerAccess: element?.monthlyValue || element?.valuePerAccess || 0,
        description: element?.planDescription || element?.description || '',
      },
      { emitEvent: true }
    );

    this.includedItems = Array.isArray(element?.includedItems) ? [...element.includedItems] : [];
    this.excludedItems = Array.isArray(element?.excludedItems) ? [...element.excludedItems] : [];
    const partnerIdsFromElement = this.extractPartnerIdsFromElement(element);
    this.pendingPartnerIds = partnerIdsFromElement;
    this.selectedPartners = [];
    // Do NOT call applyPartnerSelectionState() here — loadPartners() is async and this.partners
    // is still [] at this point. Calling it now clears pendingPartnerIds against an empty list.
    // loadPartners() will call applyPartnerSelectionState() once the partner list arrives.

    if (Array.isArray(element?.variations) && element.variations.length > 0) {
      const normalizedSystemId = String(systemId || '').trim();
      this.variations = element.variations.map((variation: any) => {
        const variationSystemId = String(variation?.systemId || '').trim();
        return {
          ...variation,
          systemId: variationSystemId || normalizedSystemId,
        };
      });
      this.variationsDataSource.data = [...this.variations];
      this.variationCounter = this.variations.length + 1;
    }

    if (Array.isArray(element?.modules) && element.modules.length > 0) {
      this.selectedModules = [...element.modules];
    }

    if (this.typeCes === TypeCesEnum.EDIT) {
      this.clearVariationFields(true);
    }

    setTimeout(() => {
      this.isHydratingInitialData = false;
    });
  }

  private extractPartnerIdsFromElement(element: any): string[] {
    const candidates: any[] = [
      ...(Array.isArray(element?.partnersList) ? element.partnersList : []),
      ...(Array.isArray(element?.partners) ? element.partners : []),
    ];

    const ids = candidates
      .map((item: any) => {
        if (typeof item === 'string' || typeof item === 'number') {
          return String(item).trim();
        }
        return String(
          item?.id ??
          item?.partnerId ??
          item?.partner?.id ??
          item?.value ??
          ''
        ).trim();
      })
      .filter((id: string) => !!id);

    return Array.from(new Set(ids));
  }

  private areAllPartnersSelected(): boolean {
    if (!Array.isArray(this.partners) || this.partners.length === 0) {
      return false;
    }
    return this.selectedPartnerIds.length === this.partners.length;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get createdAtDisplay(): Date {
    const raw =
      this.data?.element?.createdAt ||
      this.data?.element?.created_at;
    const parsed = raw ? new Date(raw) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : this.today;
  }

  public get lastUpdatedDisplay(): Date | null {
    const raw =
      this.data?.element?.updatedAt ||
      this.data?.element?.updated_at;
    const parsed = raw ? new Date(raw) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
  }

  public get changedByDisplay(): string {
    return (
      this.data?.element?.updatedBy?.firstName ||
      this.data?.element?.updatedBy?.name ||
      this.data?.element?.createdBy?.firstName ||
      this.data?.element?.createdBy?.name ||
      '--'
    );
  }

  public get totalPlanValue(): number {
    if (Array.isArray(this.variations) && this.variations.length > 0) {
      return this.variations.reduce((acc, item: any) => {
        const raw = item?.valorMensalidade ?? item?.valuePerAccess ?? 0;
        const parsed = this.parseMoneyValue(raw);
        return acc + (Number.isFinite(parsed) ? parsed : 0);
      }, 0);
    }

    const current = this.form?.get('valuePerAccess')?.value ?? 0;
    return this.parseMoneyValue(current);
  }

  public get totalCostValue(): number {
    if (Array.isArray(this.variations) && this.variations.length > 0) {
      return this.variations.reduce((acc, item: any) => {
        const raw = item?.custo ?? item?.cost ?? 0;
        const parsed = this.parseMoneyValue(raw);
        return acc + (Number.isFinite(parsed) ? parsed : 0);
      }, 0);
    }

    const current = this.form?.get('cost')?.value ?? 0;
    return this.parseMoneyValue(current);
  }

  private parseMoneyValue(value: any): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    const input = String(value ?? '').trim();
    if (!input) return 0;

    const sanitized = input.replace(/[^\d,.-]/g, '');
    const hasComma = sanitized.includes(',');
    const hasDot = sanitized.includes('.');

    if (hasComma && hasDot) {
      // pt-BR thousand separators with decimal comma: 1.234,56 -> 1234.56
      return Number(sanitized.replace(/\./g, '').replace(',', '.')) || 0;
    }

    if (hasComma) {
      // decimal comma: 12,34 -> 12.34
      return Number(sanitized.replace(',', '.')) || 0;
    }

    return Number(sanitized) || 0;
  }

  public get isPartnerUser(): boolean {
    return this.authService.isPartner();
  }
}



